import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, FileSpreadsheet } from "lucide-react";
import {
  formatTaxSummaryWorkbookTitle,
  yearFromPeriodKey,
} from "@/lib/taxSummaryWorkbookTitle";
import { buildTaxSummaryCapturePath } from "@/lib/taxSummaryWorkbookConstants";
import {
  buildTrackingMonthReturnPath,
} from "@/lib/taxSummaryNavigation";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";

export default function TaxSummaryWorkbookPanel({
  fiscalClientId,
  clientName = "",
  periodKey,
  readOnly = false,
  returnTo,
  returnLabel = "",
}) {
  const navigate = useNavigate();
  const year = useMemo(() => yearFromPeriodKey(periodKey), [periodKey]);
  const title = useMemo(
    () => formatTaxSummaryWorkbookTitle(clientName),
    [clientName],
  );

  if (!fiscalClientId || !periodKey) return null;

  const resolvedReturnTo = returnTo || buildTrackingMonthReturnPath({ fiscalClientId, periodKey });
  const resolvedReturnLabel = returnLabel || "Volver al seguimiento del mes";
  const capturePath = buildTaxSummaryCapturePath({
    fiscalClientId,
    year,
    returnTo: resolvedReturnTo,
    returnLabel: resolvedReturnLabel,
  });
  const captureUrl = clientName
    ? `${capturePath}&name=${encodeURIComponent(clientName)}`
    : capturePath;

  const goToCapture = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    syncWorkClientFromDespacho(fiscalClientId);
    navigate(captureUrl);
  };

  return (
    <button
      type="button"
      className="flex w-full items-start gap-2 rounded-sm border border-emerald-200 bg-emerald-50/80 px-3 py-2.5 text-left hover:bg-emerald-50"
      data-testid="tax-summary-workbook-open"
      onClick={goToCapture}
    >
      <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-emerald-900 line-clamp-2" title={title}>
          {title}
        </span>
        <span className="mt-0.5 block text-[11px] text-emerald-800/80">
          Libro anual {year} · plantilla ISR/IVA/retenciones, CFDI y Excel
          {readOnly ? (
            <span className="text-slate-500"> · solo lectura</span>
          ) : (
            <span className="text-emerald-700"> · abre página de captura completa</span>
          )}
        </span>
      </span>
      <ChevronRight className="shrink-0 h-4 w-4 text-emerald-800 mt-0.5" aria-hidden />
    </button>
  );
}
